/**
 * Drawing a path by hand. §6.4.
 *
 * Click to place a corner. Click and drag to place a smooth point, the drag
 * pulling out the handles either side of it. Click the first point again to
 * close the path, or press Enter to leave it open. That is the gesture every
 * vector tool has used for thirty years, and it is worth matching exactly -
 * anyone who has drawn a path before already knows this, and anyone who has not
 * will be taught it by someone who has.
 *
 * While drawing, the path lives here rather than in the document. A half-drawn
 * path is not a thing a customer owns: it has no fill worth speaking of, undo
 * should not step back through it point by point, and abandoning it should
 * leave nothing behind. It becomes a layer at the moment it is finished.
 *
 * The layer it becomes is an ordinary shape whose geometry happens to be
 * stored rather than computed, which is why nothing downstream needed telling
 * about any of this.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Circle, Group, Line, Path, Rect } from 'react-konva';
import type Konva from 'konva';
import { useEditor } from '../state/store';
import { mmToPx, pxToMm } from '../model/units';
import type { PathNode } from '../model/types';
import { freePath } from '../shapes/library';
import { extent, toUnit } from '../shapes/paths';
import { addDrawn } from '../state/actions';
import { isTyping } from './CanvasView';

const ACCENT = '#dd1d26';

/** Clicking within this of the first point closes the path rather than adding to it. */
const CLOSE_NEAR = 9;

/** A drag shorter than this was a click that wobbled, not a curve. */
const CURVE_FROM = 3;

interface Props {
  zoom: number;
  /** Called when the path is finished or abandoned. */
  onDone: () => void;
}

export function PenTool( { zoom, onDone }: Props ) {
  const view = useEditor( ( s ) => s.view );

  /** Points placed so far, in millimetres on the page. */
  const [ points, setPoints ] = useState<PathNode[]>( [] );

  /** Where the pointer is, so the segment being drawn can be seen. */
  const [ at, setAt ] = useState<{ x: number; y: number } | null>( null );

  /**
   * The anchor under the button, while it is down. Held apart from the anchor
   * as stored because the handles are measured from where the press began,
   * and the stored point may have gained handles since.
   */
  const dragging = useRef<{ x: number; y: number } | null>( null );

  /** The press landed on the first point, so letting go closes the path. */
  const closing = useRef( false );

  const placed = useRef<PathNode[]>( [] );

  placed.current = points;

  const finish = useCallback(
    ( closed: boolean ) => {
      const nodes = placed.current;

      // Two points make a line; one is a stray click and worth nothing.
      if ( nodes.length >= 2 ) addDrawn( nodes, closed );

      setPoints( [] );
      setAt( null );
      onDone();
    },
    [ onDone ]
  );

  useEffect( () => {
    const key = ( event: KeyboardEvent ) => {
      if ( isTyping( event.target ) ) return;

      const mod = event.ctrlKey || event.metaKey;
      const name = event.key;

      const mine =
        name === 'Enter' ||
        name === 'Escape' ||
        name === 'Backspace' ||
        name === 'Delete' ||
        ( mod && name.toLowerCase() === 'z' );

      if ( ! mine ) return;

      /*
       * These keys mean something else to the editor - undo the document,
       * delete the selection, type into the selected text - and the editor's
       * handler listens on the same window. Taking them in the capture phase
       * and stopping them there is what keeps one key doing one thing: Ctrl+Z
       * used to take back the last point and the last change to the document
       * both at once.
       */
      event.preventDefault();
      event.stopImmediatePropagation();

      if ( name === 'Enter' ) {
        finish( false );

        return;
      }

      if ( name === 'Escape' ) {
        setPoints( [] );
        setAt( null );
        onDone();

        return;
      }

      // Undo, and Backspace, take back the last point rather than the last
      // thing in the document - which is what a customer means by either here.
      dragging.current = null;
      setPoints( ( was ) => was.slice( 0, -1 ) );
    };

    window.addEventListener( 'keydown', key, true );

    return () => window.removeEventListener( 'keydown', key, true );
  }, [ finish, onDone ] );

  /* --- placing ---------------------------------------------- */

  const where = ( event: Konva.KonvaEventObject<MouseEvent> ) => {
    const stage = event.target.getStage();
    const point = stage?.getPointerPosition();

    if ( ! point ) return null;

    return {
      x: pxToMm( point.x - view.x, zoom ),
      y: pxToMm( point.y - view.y, zoom ),
    };
  };

  /*
   * The anchor goes down with the button, and the drag that follows shapes
   * that same anchor. It used to be added on release instead, which meant the
   * drag had only the previous point to work on - so every curve bent at the
   * point before the one being placed.
   */
  const onDown = useCallback(
    ( event: Konva.KonvaEventObject<MouseEvent> ) => {
      // The right button opens menus everywhere else; here it places nothing.
      if ( event.evt.button !== 0 ) return;

      const point = where( event );

      if ( ! point ) return;

      setAt( point );

      const first = placed.current[ 0 ];

      // Back on the first point: that is how a path is closed. Nothing is
      // added - the path already starts there.
      if ( first && placed.current.length > 1 ) {
        const near = Math.hypot( mmToPx( point.x - first.x, zoom ), mmToPx( point.y - first.y, zoom ) );

        if ( near < CLOSE_NEAR ) {
          closing.current = true;

          return;
        }
      }

      dragging.current = point;
      setPoints( ( was ) => was.concat( { x: point.x, y: point.y } ) );
    },
    [ view.x, view.y, zoom ]
  );

  const onMove = useCallback(
    ( event: Konva.KonvaEventObject<MouseEvent> ) => {
      const point = where( event );

      if ( ! point ) return;

      setAt( point );

      // The button came up somewhere this sheet never heard about - off the
      // window, or over a panel. The drag is over either way.
      if ( event.evt.buttons === 0 ) dragging.current = null;

      const from = dragging.current;

      if ( ! from ) return;

      const pulled = Math.hypot( mmToPx( point.x - from.x, zoom ), mmToPx( point.y - from.y, zoom ) );

      /*
       * The handles are mirrored about the anchor, which is what makes the
       * point smooth: the curve arrives and leaves along the same line. Pulling
       * one handle out and leaving the other alone is what an Alt-drag does,
       * and that belongs to node editing rather than to placing.
       *
       * A drag brought back to where it started is a corner again, which is
       * how a customer who pulled by accident undoes it without knowing how.
       */
      setPoints( ( was ) => {
        const last = was.length - 1;
        const node = was[ last ];

        if ( ! node ) return was;

        const next = was.slice();

        next[ last ] =
          pulled < CURVE_FROM
            ? { x: node.x, y: node.y }
            : {
                x: node.x,
                y: node.y,
                out: { x: point.x, y: point.y },
                in: { x: from.x * 2 - point.x, y: from.y * 2 - point.y },
              };

        return next;
      } );
    },
    [ view.x, view.y, zoom ]
  );

  const onUp = useCallback( () => {
    dragging.current = null;

    if ( closing.current ) {
      closing.current = false;
      finish( true );
    }
  }, [ finish ] );

  /* --- drawing what is there so far ------------------------- */

  /*
   * The segment still to come follows the pointer - but not while a point is
   * being pulled, when the pointer is holding a handle rather than showing
   * where the next point will go.
   */
  const shown = at && points.length > 0 && ! dragging.current ? points.concat( { x: at.x, y: at.y } ) : points;

  const box = extent( shown );
  const d = box
    ? freePath(
        shown.map( ( node ) => toUnit( node, box ) ),
        false,
        mmToPx( box.w, zoom ),
        mmToPx( box.h, zoom )
      )
    : '';

  const first = points[ 0 ];

  return (
    <Group>
      {/*
        A transparent sheet over the whole canvas, because the pen draws on
        paper and on the grey around it alike. Deliberately far larger than any
        view: the alternative is plumbing the stage size in and keeping it in
        step with every resize, to catch clicks that were never going to land
        outside anyway.
      */}
      <Rect
        x={ -1e5 }
        y={ -1e5 }
        width={ 2e5 }
        height={ 2e5 }
        onMouseDown={ onDown }
        onMouseMove={ onMove }
        onMouseUp={ onUp }
      />

      { box && d && (
        <Group x={ mmToPx( box.x, zoom ) + view.x } y={ mmToPx( box.y, zoom ) + view.y } listening={ false }>
          <Path data={ d } stroke={ ACCENT } strokeWidth={ 1.5 } strokeScaleEnabled={ false } />
        </Group>
      ) }

      {/* The handles of the point being pulled out, so the curve can be aimed. */}
      { points.map( ( node, index ) =>
        node.out && index === points.length - 1 ? (
          <Group key="handles" x={ view.x } y={ view.y } listening={ false }>
            <Line
              points={ [
                mmToPx( node.in?.x ?? node.x, zoom ),
                mmToPx( node.in?.y ?? node.y, zoom ),
                mmToPx( node.out.x, zoom ),
                mmToPx( node.out.y, zoom ),
              ] }
              stroke={ ACCENT }
              strokeWidth={ 1 }
              strokeScaleEnabled={ false }
              opacity={ 0.5 }
            />
          </Group>
        ) : null
      ) }

      <Group x={ view.x } y={ view.y } listening={ false }>
        { points.map( ( node, index ) => (
          <Circle
            key={ index }
            x={ mmToPx( node.x, zoom ) }
            y={ mmToPx( node.y, zoom ) }
            radius={ index === 0 ? 4.5 : 3 }
            fill="#ffffff"
            stroke={ ACCENT }
            strokeWidth={ 1.5 }
            strokeScaleEnabled={ false }
          />
        ) ) }

        {/* The first point wears a ring once closing is possible. */}
        { first && points.length > 1 && (
          <Circle
            x={ mmToPx( first.x, zoom ) }
            y={ mmToPx( first.y, zoom ) }
            radius={ 8 }
            stroke={ ACCENT }
            strokeWidth={ 1 }
            strokeScaleEnabled={ false }
            opacity={ 0.45 }
          />
        ) }
      </Group>
    </Group>
  );
}
